import { addMonths, subMonths } from 'date-fns'
import { type IETFLanguageTag, type WeekStartsOn } from '@opallabs/concepts'
import { CalendarFilterButton } from '@opallabs/filters'
import { Button, Icon, Popover } from '@opallabs/lapidary'
import { ControlBar } from '../../header/ControlBar'
import { DateNavigator } from '../../header/DateNavigator'
import { type MonthViewSettings } from '../useMonthViewSettings'
import { SortControl } from './SortControl'
import * as styles from './styles'
import { ViewMenu, type ViewMenuLabelSet } from './ViewMenu'

export { selectors as monthToolbarSelectors } from './styles'

export type MonthToolbarProps = Readonly<{
  /** How many filter values are applied. Zero hides the badge. */
  filterCount: number
  isFilterOpen: boolean
  /** Whether the details panel has anything on show. */
  isPanelShowing: boolean
  /** The workspace's label sets, one of which the month can be coloured by. */
  labelSets?: readonly ViewMenuLabelSet[]
  locale?: IETFLanguageTag
  /** Any date inside the month on show. */
  month: Date
  onMonthChange: (month: Date) => void
  /** Everything the View menu decides. */
  onSettingsChange: (change: Partial<MonthViewSettings>) => void
  onToggleFilters: () => void
  onTogglePanel: () => void
  onToday: () => void
  /**
   * The view sheet was opened. The label sets it offers are only read once
   * somebody has asked to see them.
   */
  onViewMenuOpen?: () => void
  settings: MonthViewSettings
  weekStartsOn: WeekStartsOn
}>

/**
 * The month's own row of controls. The page header above it names the page and
 * offers the timescales, so everything here only means something in Month.
 */
export const MonthToolbar: React.FC<MonthToolbarProps> = ({
  filterCount,
  isFilterOpen,
  isPanelShowing,
  labelSets,
  locale,
  month,
  onMonthChange,
  onSettingsChange,
  onToggleFilters,
  onTogglePanel,
  onToday,
  onViewMenuOpen,
  settings,
  weekStartsOn,
}) => (
  <ControlBar
    start={
      <>
        <CalendarFilterButton
          count={filterCount}
          isOpen={isFilterOpen}
          onToggle={onToggleFilters}
        />
        <SortControl
          sortBy={settings.sortBy}
          onChange={(sortBy) => onSettingsChange({ sortBy })}
        />
      </>
    }
    center={
      <DateNavigator
        label={new Intl.DateTimeFormat(locale, {
          month: 'long',
          year: 'numeric',
        }).format(month)}
        nextLabel="Go to next month"
        onDateSelect={onMonthChange}
        onNext={() => onMonthChange(addMonths(month, 1))}
        onPrevious={() => onMonthChange(subMonths(month, 1))}
        onToday={onToday}
        previousLabel="Go to previous month"
        selectedDate={month}
        // The grid redraws underneath, so a screenreader has to be told which
        // month it is now looking at.
        shouldAnnounceLabel={true}
        weekStartsOn={weekStartsOn}
      />
    }
    end={
      <>
        {/*
          A sheet, not a menu: its rows hold switches, segmented controls and a
          way into a pane of their own, and a menu's keyboard model fights all
          three. Not modal either — every row redraws the calendar behind the
          sheet, so the grid has to stay readable, and reachable by assistive
          technology, while a person is still choosing.
        */}
        <Popover.Root
          modal={false}
          onOpenChange={(isOpen) => isOpen && onViewMenuOpen?.()}
        >
          <Popover.Trigger>
            <Button
              ariaLabel="Open view settings"
              icon={<Icon ariaMode="hidden" glyph="gem-settings" />}
              name="view-settings"
              variant="ghost"
            />
          </Popover.Trigger>
          <Popover.Content
            align="end"
            aria-label="View settings"
            css={styles.viewMenuPopover}
            side="bottom"
            sideOffset={4}
          >
            {/* The sheet's own box sits inside the popover's, which is what
                carries the surface and the shadow. */}
            <div className={styles.selectors.viewMenu} css={styles.viewMenu}>
              <ViewMenu
                labelSets={labelSets}
                settings={settings}
                onChange={onSettingsChange}
              />
            </div>
          </Popover.Content>
        </Popover.Root>

        <Button
          ariaLabel={`${isPanelShowing ? 'Hide' : 'Show'} details`}
          ariaPressed={isPanelShowing}
          icon={<Icon ariaMode="hidden" glyph="gem-sidebar-right" />}
          name="toggle-panel"
          onClick={onTogglePanel}
          variant="ghost"
        />
      </>
    }
  />
)
MonthToolbar.displayName = 'Calendar: MonthToolbar'
